import React, { useState, useEffect } from 'react';
import FormModal from './FormModal';
import { FaTrashAlt, FaSearch, FaDownload, FaEye } from 'react-icons/fa';
import api from '../../services/api';

const Contacts = () => {
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filter, setFilter] = useState({ date: '', search: '' });
  const [hoveredRow, setHoveredRow] = useState(null);

  const fetchContacts = async () => {
    try {
      const data = await api.get('contacts');
      if (Array.isArray(data)) {
        setContacts(data);
        setFilteredContacts(data);
      } else {
        setContacts([]);
        setFilteredContacts([]);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setContacts([]);
      setFilteredContacts([]);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  useEffect(() => {
    let filtered = contacts;
    if (filter.date) {
      filtered = filtered.filter(contact => new Date(contact.createdAt).toLocaleDateString() === new Date(filter.date).toLocaleDateString());
    }
    if (filter.search) {
      const lower = filter.search.toLowerCase();
      filtered = filtered.filter(contact => 
        contact.name.toLowerCase().includes(lower) || 
        contact.email.toLowerCase().includes(lower) ||
        contact.phone.includes(lower)
      );
    }
    setFilteredContacts(filtered);
  }, [filter, contacts]);

  const handleView = (contact) => {
    setSelectedContact(contact);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this contact?')) {
      try {
        await api.delete(`contacts/${id}`);
        fetchContacts();
      } catch (error) {
        console.error('Error deleting contact:', error);
      }
    }
  };

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Contacts</h1>
          <p className="text-gray-500 mt-1">Manage inquiries and form submissions</p>
        </div>
        <div className="flex gap-3 w-full md:w-auto">
          <button
            onClick={async () => {
              try {
                const response = await api.download('contacts/download');
                const blob = await response.blob();
                const url = window.URL.createObjectURL(new Blob([blob]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'contacts.csv');
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
              } catch (error) {
                console.error('Error downloading contacts:', error);
              }
            }}
            className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 font-semibold py-2 px-4 rounded-lg shadow-sm flex items-center gap-2 transition-all"
          >
            <FaDownload className="text-gray-400" /> Export CSV
          </button>
        </div>
      </div>

      <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 mb-6 flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <FaSearch className="absolute left-3 top-3 text-gray-400" />
          <input 
            type="text" 
            placeholder="Search by name, email, phone..." 
            value={filter.search}
            onChange={(e) => setFilter(prev => ({ ...prev, search: e.target.value }))}
            className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
          />
        </div>
        <input
          type="date"
          name="date"
          value={filter.date}
          onChange={(e) => setFilter(prev => ({ ...prev, date: e.target.value }))}
          className="p-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent shadow-sm text-gray-600"
        />
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50/50 border-b border-gray-100">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Contact Info</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Company</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredContacts.map((contact) => (
                <tr 
                  key={contact.id} 
                  className="group hover:bg-gray-50/80 transition-colors"
                  onMouseEnter={() => setHoveredRow(contact.id)}
                  onMouseLeave={() => setHoveredRow(null)}
                >
                  <td className="px-6 py-4">
                    <div className="font-semibold text-gray-900">{contact.name}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{contact.email}</div>
                    <div className="text-xs text-gray-500">{contact.phone}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{contact.company || '-'}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {new Date(contact.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleView(contact)}
                        className="p-2 text-gray-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-colors"
                        title="View Details"
                      >
                        <FaEye />
                      </button>
                      <button
                        onClick={() => handleDelete(contact.id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <FaTrashAlt />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredContacts.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-400 bg-gray-50/30">
                    No contacts found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {selectedContact && <FormModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} formData={selectedContact} />}
    </div>
  );
};

export default Contacts;